import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import { faqs, pricingFaqs } from "@/lib/finendra-data";

const URL = "https://finendra.com/faq";
const OG = "https://finendra.com/og/og-default.png";

const allItems = [
  { title: "General", items: faqs.map((f) => ({ q: f.q, a: f.a })) },
  ...pricingFaqs,
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ | Finendra Outsourced Accounting Questions" },
      {
        name: "description",
        content:
          "Answers on onboarding time, software, catch-up bookkeeping, data security, billing cadence, and how Finendra's fixed monthly pricing works.",
      },
      { property: "og:title", content: "Frequently Asked Questions | Finendra" },
      {
        property: "og:description",
        content:
          "Everything clients ask before starting: scope, pricing, security, software, and onboarding.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: allItems.flatMap((g) =>
            g.items.map((i) => ({
              "@type": "Question",
              name: i.q,
              acceptedAnswer: { "@type": "Answer", text: i.a },
            })),
          ),
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <>
      <PageHero
        eyebrow="Answers"
        title="Frequently asked questions."
        body="Scope, pricing, software, security, and onboarding — the questions we hear most from finance leaders and founders."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-4xl space-y-16">
          {allItems.map((group) => (
            <div key={group.title}>
              <Reveal>
                <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                  {group.title}
                </h2>
              </Reveal>
              <dl className="mt-6 divide-y divide-brand-navy/10 border-y border-brand-navy/10">
                {group.items.map((item, i) => (
                  <Reveal key={item.q} delay={i * 0.04} className="py-7">
                    <dt className="font-display text-lg font-bold text-brand-navy">{item.q}</dt>
                    <dd className="mt-3 leading-relaxed text-brand-navy/60">{item.a}</dd>
                  </Reveal>
                ))}
              </dl>
            </div>
          ))}

          <Reveal className="rounded-3xl border border-brand-navy/10 bg-brand-surface p-8 text-center">
            <p className="text-brand-navy/60">Still have a question about your specific setup?</p>
            <Link
              to="/contact"
              className="mt-4 inline-block rounded-full bg-brand-navy px-7 py-3 font-semibold text-background transition-colors hover:bg-brand-accent"
            >
              Ask our team
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-12">
        <LeadMagnet defaultIndustry="General Startup FAQ" />
      </section>

      <CtaPanel />
    </>
  );
}
